import React, { useEffect, useMemo, useState } from 'react';
import api, { downloadApiFile, extractApiError, isRequestCanceled, unwrapApiResponse } from '../api/api';
import { formatDuration, formatRowsProgress } from '../utils/duration';
import { useToast } from './ToastProvider';

function StatItem({ label, value }) {
  return (
    <div className="job-detail-stat">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function JobDetailPanel({ jobId, getToken, onClose }) {
  const { showError, showSuccess } = useToast();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) return undefined;
    let timeoutId;
    let alive = true;
    let controller;

    async function loadJob() {
      controller?.abort?.();
      controller = new AbortController();
      try {
        setLoading(true);
        const token = await getToken({ skipCache: true });
        const response = await api.get(`/ingestion/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });
        if (!alive) return;
        const payload = unwrapApiResponse(response);
        setJob(payload);
        setLoading(false);
        if (['queued', 'processing', 'canceling'].includes(payload?.status)) {
          timeoutId = setTimeout(loadJob, 2000);
        }
      } catch (error) {
        if (!alive || isRequestCanceled(error)) return;
        setLoading(false);
        showError(extractApiError(error, 'No se pudo cargar el detalle del job'));
      }
    }

    loadJob();
    return () => {
      alive = false;
      controller?.abort?.();
      clearTimeout(timeoutId);
    };
  }, [getToken, jobId, showError]);

  const artifactRows = useMemo(() => job?.artifacts || [], [job]);
  const fileRows = useMemo(() => job?.files || [], [job]);

  async function handleDownload(artifact) {
    try {
      const token = await getToken({ skipCache: true });
      await downloadApiFile(`/ingestion/jobs/${job.id}/artifacts/download?path=${encodeURIComponent(artifact.relativePath)}`, token, artifact.name);
      showSuccess(`Descarga iniciada: ${artifact.name}`, { duration: 2500 });
    } catch (error) {
      if (isRequestCanceled(error)) return;
      showError(extractApiError(error, 'No se pudo descargar el artifact'));
    }
  }

  if (!jobId) return null;

  return (
    <>
      <button type="button" className="job-detail-backdrop" onClick={onClose} aria-label="Cerrar detalle" />
      <aside className="job-detail-panel table-card">
        <div className="job-detail-header">
          <div>
            <div className="brand-badge">Detalle</div>
            <h3 className="section-title with-margin">{job?.name || jobId}</h3>
            <p className="section-subtitle">{job?.latestMessage || 'Sin mensaje disponible'}</p>
          </div>
          <button type="button" className="secondary-button" onClick={onClose}>Cerrar</button>
        </div>

        {loading && !job ? <p className="section-subtitle">Cargando detalle...</p> : null}

        {job ? (
          <div className="job-detail-content">
            <div className="job-detail-stats-grid">
              <StatItem label="Estado" value={job.status} />
              <StatItem label="Config" value={job.configName || '-'} />
              <StatItem label="Carpeta" value={job.sourceFolderName || '-'} />
              <StatItem label="Duración" value={job.durationLabel || formatDuration(job.durationMs)} />
              <StatItem label="Archivos" value={`${job.successFiles || 0}/${job.totalFiles || job.uploadedFiles || 0}`} />
              <StatItem label="Rows" value={job.rowsProgressLabel || formatRowsProgress(job.insertedRows, job.totalRows)} />
              <StatItem label="Rechazadas" value={job.rejectedRows || 0} />
            </div>

            <div className="job-detail-section">
              <h4 className="section-title">Resumen</h4>
              <div className="job-detail-info-grid">
                <div><span>Creado</span><strong>{job.createdAt || '-'}</strong></div>
                <div><span>Iniciado</span><strong>{job.startedAt || '-'}</strong></div>
                <div><span>Finalizado</span><strong>{job.finishedAt || '-'}</strong></div>
                <div><span>Error</span><strong>{job.error || '-'}</strong></div>
              </div>
            </div>

            <div className="job-detail-section">
              <h4 className="section-title">Tiempos por etapa</h4>
              <div className="job-detail-stats-grid">
                <StatItem label="Espera en cola" value={job.stageTimingLabels?.queueWait || formatDuration(job.stageTimings?.queueWaitMs)} />
                <StatItem label="Preparación" value={job.stageTimingLabels?.inputPreparation || formatDuration(job.stageTimings?.inputPreparationMs)} />
                <StatItem label="Worker" value={job.stageTimingLabels?.workerExecution || formatDuration(job.stageTimings?.workerExecutionMs)} />
                <StatItem label="Extracción ZIP" value={job.stageTimingLabels?.zipExtraction || formatDuration(job.stageTimings?.zipExtractionMs)} />
                <StatItem label="Procesamiento" value={job.stageTimingLabels?.fileProcessing || formatDuration(job.stageTimings?.fileProcessingMs)} />
                <StatItem label="Total ciclo" value={job.stageTimingLabels?.totalLifecycle || formatDuration(job.stageTimings?.totalLifecycleMs)} />
              </div>
            </div>

            <div className="job-detail-section">
              <div className="section-inline-header">
                <h4 className="section-title">Artifacts</h4>
                <span className="section-subtitle">{artifactRows.length} disponible(s)</span>
              </div>
              {artifactRows.length ? (
                <div className="job-detail-list">
                  {artifactRows.map((artifact) => (
                    <div key={artifact.relativePath} className="job-detail-list-item">
                      <div>
                        <strong>{artifact.name}</strong>
                        <span>{artifact.type} · {artifact.relativePath}</span>
                      </div>
                      <button type="button" className="secondary-button small-action-button" onClick={() => handleDownload(artifact)}>Descargar</button>
                    </div>
                  ))}
                </div>
              ) : <p className="section-subtitle">Sin artifacts disponibles.</p>}
            </div>

            <div className="job-detail-section">
              <div className="section-inline-header">
                <h4 className="section-title">Archivos procesados</h4>
                <span className="section-subtitle">{fileRows.length} registro(s)</span>
              </div>
              {fileRows.length ? (
                <div className="table-shell report-table-shell">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Archivo</th>
                        <th>Estado</th>
                        <th>Rows</th>
                        <th>Rechazadas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fileRows.map((file, index) => (
                        <tr key={`${file.fileName || file.name || 'file'}-${index}`}>
                          <td>{file.fileName || file.name || file.relativePath || '-'}</td>
                          <td>{file.status || '-'}</td>
                          <td>{formatRowsProgress(file.insertedRows, file.totalRows)}</td>
                          <td>{file.rejectedRows || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="section-subtitle">Sin detalle de archivos disponible.</p>}
            </div>
          </div>
        ) : null}
      </aside>
    </>
  );
}
